import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTask } from '@/hooks/useTasks';
import { useSets } from '@/hooks/useSets';
import TaskResources from './TaskResources';
import TaskActions from './TaskActions';
import TaskMessageModal from './TaskMessageModal';
import MarkdownRenderer from '@/components/common/MarkdownRenderer';
import Badge from '@/components/common/Badge';
import CopyButton from '@/components/common/CopyButton';
import Spinner from '@/components/common/Spinner';
import Button from '@/components/common/Button';

export default function TaskDetail() {
  const { id: taskId } = useParams<{ id: string }>();
  const { data: task, isLoading, error } = useTask(taskId);
  const { data: setsData } = useSets('active');
  const [showMessageModal, setShowMessageModal] = useState(false);

  if (isLoading) return <Spinner className="py-12" />;

  if (error || !task) {
    return (
      <div className="p-8 text-center text-sm text-error-600">
        {error ? 'Failed to load task' : 'Task not found'}
      </div>
    );
  }

  // Resolve set name
  const getSetName = (): string => {
    if (!task.set_id) return 'General';
    const set = setsData?.sets.find((s) => s.set_id === task.set_id);
    return set?.name ?? 'General';
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Badge variant="primary">{getSetName()}</Badge>
            {task.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="default">
                {tag}
              </Badge>
            ))}
          </div>
          {task.status === 'active' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowMessageModal(true)}
              className="flex-shrink-0"
            >
              <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Message
            </Button>
          )}
        </div>
        <h1 className="text-2xl font-bold text-neutral-900">{task.title}</h1>
      </div>

      {/*
      {/ * Description * /}
      <section className="mb-6">
        <MarkdownRenderer content={task.description} />
      </section>
      */}

      {/* Rationale */}
      {task.rationale && (
        <section className="mb-6 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-300/20 shadow-md shadow-black/30 p-5">
          <h2 className="mb-1 text-sm font-semibold text-primary-700">Why this task?</h2>
          <p className="text-sm text-primary-800"><MarkdownRenderer content={task.rationale} /></p>
        </section>
      )}

      {/* Steps */}
      {task.steps && task.steps.length > 0 && (
        <section className="mb-6 rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
          <h2 className="mb-3 text-lg font-semibold text-neutral-900">Steps</h2>
          <ol className="space-y-4">
            {task.steps.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-neutral-700"><MarkdownRenderer content={step.instruction} /></p>
                  {step.copyable_content && (
                    <div className="mt-2 rounded-lg border border-neutral-400/20 bg-neutral-100 p-3">
                      <pre className="whitespace-pre-wrap text-sm text-neutral-700">
                      <MarkdownRenderer content={step.copyable_content} />
                      </pre>
                      <CopyButton text={step.copyable_content} label="Copy" className="mt-2" />
                    </div>
                  )}
                  {step.link && (
                    <a
                      href={step.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      {step.link.label}
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Resources */}
      {task.resources && task.resources.length > 0 && (
        <section className="mb-8 rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
          <h2 className="mb-3 text-lg font-semibold text-neutral-900">Resources</h2>
          <TaskResources
            resources={task.resources}
            taskId={taskId}
            editable={task.status === 'active'}
          />
        </section>
      )}

      {/* Actions (only for active tasks) */}
      {task.status === 'active' && taskId && <TaskActions taskId={taskId} />}

      {/* Status badge for completed/ignored tasks */}
      {task.status !== 'active' && (
        <div className="mt-4 text-center">
          <Badge variant={task.status === 'completed' ? 'success' : 'default'}>
            {task.status === 'completed' ? '✓ Completed' : 'Ignored'}
          </Badge>
        </div>
      )}

      {/* User notes for completed/ignored tasks */}
      {task.status !== 'active' && task.user_notes && (
        <div className="mt-4 rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
          <h2 className="mb-1 text-sm font-semibold text-neutral-600">Your Notes</h2>
          <p className="text-sm text-neutral-700 whitespace-pre-wrap">{task.user_notes}</p>
        </div>
      )}

      {/* Message modal */}
      <TaskMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        taskTitle={task.title}
      />
    </div>
  );
}
